import React from "react";

type FeeBreakdownRow = {
  label: string;
  value: number;
};

type FeeBreakdownBoxProps = {
  rows: FeeBreakdownRow[];
  totalLabel: string;
  totalValue: number;
  className?: string;
  loading?: boolean;
  status?: string;
  statusLabel?: string;
};

export const FeeBreakdownBox = ({
  rows,
  totalLabel,
  totalValue,
  className = "",
  loading = false,
  status,
  statusLabel = "Payment Status",
}: FeeBreakdownBoxProps) => {
  const getStatusLabel = (rawStatus?: string) => {
    const normalized = String(rawStatus || "").toLowerCase();
    switch (normalized) {
      case "requested":
        return "Requested";
      case "hold":
        return "Hold";
      case "captured":
        return "Captured";
      case "transferred":
        return "Transferred";
      case "cancelled":
        return "Cancelled";
      case "completed":
        return "Completed";
      default:
        return rawStatus ? rawStatus : "—";
    }
  };

  const getStatusPillClass = (rawStatus?: string) => {
    const normalized = String(rawStatus || "").toLowerCase();
    if (["transferred", "completed"].includes(normalized)) {
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    }
    if (["cancelled"].includes(normalized)) {
      return "bg-rose-50 text-rose-700 border-rose-200";
    }
    if (["hold", "captured"].includes(normalized)) {
      return "bg-amber-50 text-amber-800 border-amber-200";
    }
    if (["requested"].includes(normalized)) {
      return "bg-sky-50 text-sky-700 border-sky-200";
    }
    return "bg-white text-neutral-700 border-neutral-200";
  };

  if (loading) {
    const rowCount = Math.max(rows?.length || 2, 2);
    return (
      <div
        className={`rounded-lg bg-neutral-50 border border-neutral-200 px-3 pt-2 ${
          status ? "pb-0" : "pb-2"
        } animate-pulse ${className}`}
      >
        <div className="grid grid-cols-2 gap-y-2 text-sm pt-1">
          {Array.from({ length: rowCount }).map((_, idx) => (
            <React.Fragment key={idx}>
              <div className="h-3 w-24 bg-neutral-200 rounded" />
              <div className="h-3 w-16 bg-neutral-200 rounded ml-auto" />
            </React.Fragment>
          ))}
        </div>
        <div className="my-2 border-t border-dashed border-neutral-300" />
        <div className="grid grid-cols-2 gap-y-2 text-sm">
          <div className="h-3 w-24 bg-neutral-200 rounded" />
          <div className="h-3 w-20 bg-neutral-200 rounded ml-auto" />
        </div>
        {status ? (
          <div className="-mx-3 mt-2 px-3 py-2 border-t border-neutral-200 flex items-center justify-between bg-white rounded-b-lg">
            <div className="h-3 w-28 bg-neutral-200 rounded" />
            <div className="h-6 w-24 bg-neutral-200 rounded-full" />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg bg-neutral-50 border border-neutral-200 px-3 pt-2 ${
        status ? "pb-0" : "pb-2"
      } ${className}`}
    >
      <div className="grid grid-cols-2 gap-y-1 text-sm text-neutral-800 pt-1">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <span className="text-neutral-600">{row.label}</span>
            <span className="text-right tabular-nums font-medium">
              ${Number(row.value || 0).toFixed(2)}
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="my-2 border-t border-dashed border-neutral-300" />
      <div className="grid grid-cols-2 text-sm font-semibold text-neutral-900">
        <span>{totalLabel}</span>
        <span className="text-right tabular-nums">
          ${Number(totalValue || 0).toFixed(2)}
        </span>
      </div>
      {status ? (
        <div className="-mx-3 mt-2 px-3 py-2 border-t border-neutral-200 flex items-center justify-between bg-white rounded-b-lg">
          <div className="text-xs font-medium text-neutral-600">
            {statusLabel}
          </div>
          <div
            className={`text-xs font-semibold border rounded-md px-3 h-6 inline-flex items-center leading-none capitalize ${getStatusPillClass(
              status
            )}`}
          >
            {getStatusLabel(status)}
          </div>
        </div>
      ) : null}
    </div>
  );
};
